import request from '../../../../api/request';
import { PaginationRequestType } from '../../../../types/paginationRequestType';

/**
 * DISCOUNTS
 */
const DISCOUNTS = {
  DELETE: (discountId: number) =>
    `/maintainers-portal/admission-and-enrollments/discounts/${discountId}`,
  DISCOUNTS: '/maintainers-portal/admission-and-enrollments/discounts',
  SEARCH_DISCOUNTS:
    '/maintainers-portal/admission-and-enrollments/discounts/search',
};

type Modify<T, R> = Omit<T, keyof R> & R;

export interface PeriodWithDates {
  periodId: number;
  enrollmentStartDate?: string | null;
  enrollmentEndDate?: string | null;
}

export interface Discount {
  id?: number;
  code: string;
  name: string;
  description: string | null;
  percentage?: number | null;
  hasAutomaticAssignment?: boolean;
  applyToAllNewAdmissionProcesses?: boolean;
  minimumGradeAverage?: number | null;
  // Beneficio (HU 16116 v2)
  benefitTypeId?: number;
  // Tipo de beneficio (adjuntado en el listado para la columna "Tipo").
  benefitType?: { id: number; code: string; name: string } | null;
  appliesToArancel?: boolean;
  appliesToMatricula?: boolean;
  // 'porcentaje_fijo' | 'porcentaje_maximo' | 'monto_fijo' | 'monto_variable'
  // | 'monto_maximo'
  applicationType?: string | null;
  amount?: number | null;
  canCombineWithOtherBenefits?: boolean;
  inicioAcademicLevelIds?: number[];
  inicioPeriodIds?: number[];
  inicioStudyPlanIds?: number[];
  inicioCampusIds?: number[];
  inicioScheduleIds?: number[];
  inicioModalityIds?: number[];
  continuidadAcademicLevelIds?: number[];
  continuidadStudyPlanIds?: number[];
  continuidadCampusIds?: number[];
  continuidadScheduleIds?: number[];
  continuidadModalityIds?: number[];
  continuidadPeriods?: PeriodWithDates[];
  // Condiciones del pronto pago (HU 16376)
  earlyPaymentScope?: 'cash' | 'instalments' | 'both' | null;
  earlyPaymentCashFrom?: string | null;
  earlyPaymentCashTo?: string | null;
  earlyPaymentInstalmentsFrom?: string | null;
  earlyPaymentInstalmentsTo?: string | null;
}

export interface DiscountWithRelations extends Discount {
  benefitType?: { id: number; code: string; name: string } | null;
  inicioAcademicLevels?: { id: number; academicLevelId: number }[];
  inicioPeriods?: { id: number; periodId: number }[];
  inicioStudyPlans?: { id: number; studyPlanId: number }[];
  inicioCampuses?: { id: number; campusId: number }[];
  inicioSchedules?: { id: number; scheduleId: number }[];
  inicioModalities?: { id: number; modalityId: number }[];
  continuidadAcademicLevels?: { id: number; academicLevelId: number }[];
  continuidadStudyPlans?: { id: number; studyPlanId: number }[];
  continuidadCampuses?: { id: number; campusId: number }[];
  continuidadSchedules?: { id: number; scheduleId: number }[];
  continuidadModalities?: { id: number; modalityId: number }[];
  continuidadPeriods?: {
    id: number;
    periodId: number;
    enrollmentStartDate: string | null;
    enrollmentEndDate: string | null;
  }[];
}

export interface DiscountFormOption {
  id: number;
  name: string;
}

// Tipo de Beneficio (HU 16116 v2). El nombre del beneficio es texto libre.
export interface BenefitTypeOption {
  id: number;
  code: string;
  name: string;
}

export interface DiscountFormOptions {
  academicLevels: DiscountFormOption[];
  periods: DiscountFormOption[];
  studyPlans: DiscountFormOption[];
  campuses: DiscountFormOption[];
  schedules: DiscountFormOption[];
  modalities: DiscountFormOption[];
  benefitTypes: BenefitTypeOption[];
}

export interface DiscountFields
  extends Modify<Discount, { percentage: string }> {}

export type DiscountsListResponse = PaginationRequestType<Discount>;

/**
 *Obtiene el listado de todos los descuentos
 */
export const getAllDiscounts = () => {
  const url = `${DISCOUNTS.DISCOUNTS}`;
  return request<Discount[]>(url);
};

/**
 * Obtiene el listado de descuentos con paginación
 */
export const getDiscountsRequest = (items: number = 10, page: number = 0) => {
  return request<DiscountsListResponse>(DISCOUNTS.SEARCH_DISCOUNTS, {
    params: { items_per_page: items, page },
  });
};

export const saveDiscountRequest = (data: Discount) => {
  const editing = !!data.id;
  return request<Discount>(`${DISCOUNTS.DISCOUNTS}/${editing ? data.id : ''}`, {
    method: editing ? 'put' : 'post',
    data,
  });
};

export const deleteDiscountRequest = (discountId: number) => {
  return request<Discount>(DISCOUNTS.DELETE(discountId), {
    method: 'delete',
  });
};

/**
 * Obtiene las opciones para los dropdowns del formulario de descuentos
 */
export const getDiscountFormOptions = () => {
  return request<DiscountFormOptions>(`${DISCOUNTS.DISCOUNTS}/options`);
};

/**
 * Obtiene un descuento por ID con todas sus relaciones
 */
export const getDiscountById = (discountId: number) => {
  return request<DiscountWithRelations>(`${DISCOUNTS.DISCOUNTS}/${discountId}`);
};
